import React, { FC } from 'react';
import { useVitalMetric } from '@funtech-int/yango-shared';

import { playerMovieFirstFrame } from '@/scenarios/player/lib/metrics';

import { ContentPlayerOwnProps } from '../../../../containers/ContentPlayer/ContentPlayer.types';
import { Player } from '../../../Player/Player';
import { useEpisodeHandlers } from '../../hooks/use-episode-handlers';
import { useEpisodeStreams } from '../../hooks/use-episode-streams';
import { useEmptyStreamsGuard } from '../../../feature-player/hooks/player-instance/use-empty-streams-guard';
import { PlayerTracker } from '../../../feature-player/ui/player-tracker/component';
import { EpisodePlayerTracker } from '../../../feature-player/ui/episode/episode-player-tracker/component';
import { EpisodePlayerView } from '../../../feature-player/ui/episode/episode-player-view/component';
import { EpisodePlayerController } from '../../../feature-player/ui/episode/episode-player-controller/component';
import { getTimingsInterval } from '../../../player-config/helpers/get-timings-interval';
import { useConcurrencyArbiter } from '../../../feature-player/hooks/concurrency-arbiter/use-concurrency-arbiter';

import { useEpisodeTvSeriesContentId } from './hooks/use-episode-tv-series-content-id';
import { useEpisodeSeasonContentId } from './hooks/use-episode-season-content-id';
import { useEpisodeConcurrencyArbiterConfig } from './hooks/use-episode-concurrency-arbiter-config';

interface EpisodePlayerProps extends ContentPlayerOwnProps {
    contentId: string;
}

export const EpisodePlayer: FC<EpisodePlayerProps> = ({ contentId, onClose }) => {
    const { handleClose, handleWatchedTimeChange } = useEpisodeHandlers({ contentId, onClose });

    useEmptyStreamsGuard(useEpisodeStreams(contentId));
    const firstFrameDuration = useVitalMetric(playerMovieFirstFrame);

    const concurrencyArbiterConfig = useEpisodeConcurrencyArbiterConfig(contentId ?? '');
    const tvSeriesContentId = useEpisodeTvSeriesContentId(contentId ?? '');
    const seasonContentId = useEpisodeSeasonContentId(contentId ?? '');

    useConcurrencyArbiter({
        config: concurrencyArbiterConfig,
    });

    return (
        <Player
            contentId={contentId}
            onClose={handleClose}
            onBeforeInit={firstFrameDuration.begin}
            onStart={firstFrameDuration.end}
            renderPlayer={({
                contentId,
                onStartCallback,
                handleError,
                handleFinish,
                psid,
                onClose,
            }) => (
                <PlayerTracker>
                    <EpisodePlayerController
                        tvSeriesId={tvSeriesContentId}
                        timingsInterval={getTimingsInterval()}
                        contentId={contentId}
                        onContentImpression={onStartCallback}
                        onFinish={handleFinish}
                        onError={handleError}
                        onWatchedTimeChanged={handleWatchedTimeChange}
                    />
                    <EpisodePlayerTracker contentId={contentId} psid={psid} />

                    <EpisodePlayerView
                        contentId={contentId}
                        filmId={tvSeriesContentId}
                        seasonId={seasonContentId}
                        onClose={onClose}
                        psid={psid}
                    />
                </PlayerTracker>
            )}
        />
    );
};
